import { Account } from '~/types/account';
import { Category, Subcategory } from '~/types/category';
import { FiscalRegime, LogoUrls } from '~/types/company';
import { Counterpart } from '~/types/counterparts';
import { DocumentStatus, DocumentType, EInvoiceType, WithholdingTaxType } from '~/types/document';
import { FlowPaymentStatus, PaymentMethod } from '~/types/flow';
import { EsigibilitaIva, Natura, StampType, TipoCassa } from '~/types/invoicing';
import { CountryIsoCode } from './country';

export interface PaymentMethodForm {
  accountId: Account['id'] | null;
  institutionName: string | null;
  paymentMethod: PaymentMethod;
  paymentAccount: string | null;
}

interface Destination {
  address: string | null;
  postalCode: string | null;
  city: string | null;
  country: CountryIsoCode | null;
}

export interface TransportInformations {
  destination: Destination;
  transportReason: string | null;
  courier: string | null;
  packsNumber: string | null;
  weight: string | null;
}

export interface DocumentBuilderFlowForm {
  remoteId: string | null;
  amount: string;
  expectedPaymentDate: Date;
  notes: string;
  paymentStatus: FlowPaymentStatus;
}

export enum DiscountType {
  Percentage = 'PERCENTAGE',
  Amount = 'AMOUNT',
}

export interface DocumentBuilderLineDiscount {
  value: string;
  type: DiscountType;
}
export interface DocumentBuilderLine {
  code: string;
  articleCodes?: ArticleCode[];
  description: string;
  extendedDescription: string;
  discount: DocumentBuilderLineDiscount;
  quantity: string;
  unit: string;
  netPrice: string;
  grossPrice: string;
  vat: DocumentBuilderLineVAT;
  managementData: OtherManagementData[];
  competencyStartDate: Date | null;
  competencyEndDate: Date | null;
}

export interface DocumentBuilderLineVAT {
  percentage: string;
  taxExemption: Natura | null;
  lawReference: string | null;
}

interface ArticleCode {
  type: string;
  value: string;
}

export interface OtherManagementData {
  dataType: string;
  text: string | null;
  date: Date | null;
  number: string | null;
}

export interface Stamp {
  type: StampType;
  value: string;
}

export interface DocumentBuilderForm {
  counterpart: Counterpart | null;
  categoryId: Category['id'] | null;
  currency: string;
  fiscalRegime: FiscalRegime;
  date: Date;
  flows: DocumentBuilderFlowForm[];
  isEInvoice: boolean;
  includePrices: boolean | null;
  items: DocumentBuilderLine[];
  motivation: string | null;
  notes: string | null;
  paymentInfo: PaymentMethodForm;
  relatedDocuments: RelatedDocumentLine[];
  subcategoryId: Subcategory['id'] | null;
  type: DocumentType;
  subtype: EInvoiceType | null;
  vatCollection: EsigibilitaIva | null;
  withholdingTax: WithholdingTaxForm[];
  socialSecurity: SocialSecurityForm | null;
  riferimentoAmministrazione: string | null;
  stamp: Stamp | null;
  transportInformations: TransportInformations | null;
  attachmentsSize?: number;
  useGrossPrices: boolean;
}

export enum RelatedDocumentType {
  OrdineAcquisto = 'OrdineAcquisto',
  Contratto = 'Contratto',
  Convenzione = 'Convenzione',
  Ricezione = 'Ricezione',
  FatturaCollegata = 'FatturaCollegata',
  Ddt = 'Ddt',
}

export interface RelatedDocumentLine {
  dbDocumentId: string | null;
  type: RelatedDocumentType;
  date: Date | null;
  documentNumber: string;
  CIGCode: string;
  CUPCode: string;
  orderCode: string;
}

export enum TaxableMode {
  Percentage = 'PERCENTAGE',
  Amount = 'AMOUNT',
}

export interface WithholdingTaxForm {
  amountPercentage: string;
  taxableAmount: string;
  percentage: string;
  motivation: string;
  type: WithholdingTaxType;
  taxableMode: TaxableMode;
}

export interface SocialSecurityForm {
  amountPercentage: string;
  percentage: string;
  taxableAmount: string;
  type: TipoCassa;
}

export interface PreviewAdditionalData {
  companyLogo: LogoUrls;
  type: DocumentType;
  isElectronic: boolean;
  notes: string | null;
  status: DocumentStatus;
}

export interface DocumentDataLine extends Omit<DocumentBuilderLine, 'discount'> {
  discounts: DocumentBuilderLineDiscount[];
}
